/**
 * Meta repo — single-row key/value bag in Dexie.
 *
 * Stores:
 *   - anonUserId         — anon-${uuid} generated on first launch when no Cognito session.
 *   - lastSyncAt         — ISO timestamp of last successful pull.
 *   - activeSession      — snapshot of in-progress TrackingService state (survives app kill).
 *   - anonymousBannerDismissedAt — ISO timestamp; null/missing = not dismissed.
 *
 * Single-row pattern: each row is { key: 'foo', value: any }.
 */

import { v4 as uuidv4 } from 'uuid';
import { db } from './index';

export async function getMeta(key) {
  const row = await db.meta.get(key);
  return row?.value ?? null;
}

export async function setMeta(key, value) {
  await db.meta.put({ key, value });
}

export async function deleteMeta(key) {
  await db.meta.delete(key);
}

/**
 * Get or create the anonymous user ID. Persists across reloads.
 * @returns {Promise<string>}
 */
export async function getOrCreateAnonUserId() {
  const existing = await getMeta('anonUserId');
  if (existing) return existing;
  const id = `anon-${uuidv4()}`;
  await setMeta('anonUserId', id);
  return id;
}

export async function getAnonUserId() {
  return getMeta('anonUserId');
}

/**
 * Active in-progress session snapshot. Used to resume after app kill.
 * @typedef {Object} ActiveSessionSnapshot
 * @property {string} state             RecordingState
 * @property {string} sessionId
 * @property {number} startTime         ms epoch
 * @property {number} pauseAccumulator  ms paused
 * @property {number | null} pauseStart ms epoch when current pause began
 * @property {Array} rawPoints          GPS point array
 * @property {string | null} resortId
 * @property {string | null} resortName
 */

export async function saveActiveSession(snapshot) {
  await setMeta('activeSession', snapshot);
}

export async function loadActiveSession() {
  return getMeta('activeSession');
}

export async function clearActiveSession() {
  await deleteMeta('activeSession');
}
